import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import StaffDoctorsList from './components/StaffDoctorsList';
import banner from './assets/giphy.jpg';
import giphyImg from './assets/giphyyy.jpg';
import './tokenlyglobal.css';
import './tokenly.css';

const DoctorDashboard = () => {
    const navigate = useNavigate();
    const [isLoading, setLoading] = useState(false);
    const [dashData, setDashData] = useState(null);
    const [currentTime, setCurrentTime] = useState(moment());
    const token = localStorage.getItem("tokenly_token");
    const userData = JSON.parse(localStorage.getItem("tokenly_user") || "{}");

    // ─── Fetch Main Dashboard Data (v15) ─────────────────────────────────────
    const fetchDashboard = useCallback(async (isBackground = false) => {
        if (!isBackground) setLoading(true);
        try {
            const res = await fetch("https://tokenly.biswaas.com/api/doctor/doctorDashboardDataV15", {
                method: "POST",
                headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
                body: JSON.stringify({ version: 15 })
            });
            const json = await res.json();
            if (json.status) {
                setDashData(json.data);
            } else {
                if (!isBackground) alert(json.message || "Failed to load dashboard");
            }
        } catch (err) {
            console.error("Fetch Error:", err);
            if (!isBackground) alert("Network error connecting to dashboard.");
        } finally {
            if (!isBackground) setLoading(false);
        }
    }, [token]);

    // ─── Init & Polling ──────────────────────────────────────────────────────
    useEffect(() => {
        if (!token) { navigate('/login'); return; }

        // Initial Fetch
        fetchDashboard();

        // 🔄 Global Sync: Har 20 seconds mein dashboard refresh hoga (Background Refresh)
        const pollTimer = setInterval(() => fetchDashboard(true), 20000);

        return () => {
            clearInterval(pollTimer);
        };
    }, [token, navigate, fetchDashboard]);

    // ─── Live Clock Effect ─────────────────────────────────────────────────────
    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentTime(moment());
        }, 1000);
        return () => clearInterval(timer);
    }, []);

    if (!dashData && isLoading) {
        return (
            <div className="d-flex justify-content-center align-items-center vh-100 bg-light">
                <div className="text-center">
                    <div className="spinner-border text-primary" role="status"></div>
                    <p className="mt-2 fw-medium text-muted">Refreshing Dashboard...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="dashboard-wrapper bg-light" style={{ paddingTop: '110px' }}>

            <div className="container-fluid" style={{ padding: '0' }}>
                {/* Desktop Layout */}
                <div className="row g-4 align-items-start d-none d-lg-flex">
                    {/* Left Side Banner */}
                    <div className="col-lg-4">
                        <div className="sticky-top" style={{ top: '90px', height: '80vh' }}>
                            <img src={giphyImg} className="img-fluid rounded-4 w-100 h-100" style={{ objectFit: 'contain', objectPosition: 'center' }} alt="Left Banner" />
                        </div>
                    </div>

                    {/* Main Content Area */}
                    <div className="col-lg-4">
                        {/* Compact Header Box */}
                        <div className="card border-0 shadow-sm rounded-4 mb-4 bg-white">
                            <div className="card-body py-2 px-3">
                                <div className="row align-items-center">
                                    <div className="col-md-7">
                                        <h6 className="mb-0 fw-bold text-dark" style={{ fontSize: '15px' }}>{userData?.name || dashData?.clinic_name || 'Doctor Dashboard'}</h6>
                                        <p className="text-muted mb-0 fw-bold" style={{ fontSize: '12px' }}>Live Overview & Token Management</p>
                                    </div>
                                    <div className="col-md-5 text-md-end mt-1 mt-md-0">
                                        <p className="text-muted mb-1 fw-bold" style={{ fontSize: '12px' }}>{moment().format('dddd, DD MMMM YYYY')}</p>
                                        <p className="text-primary mb-0 fw-bold" style={{ fontSize: '18px', fontFamily: 'monospace', letterSpacing: '2px' }}>{currentTime.format('HH:mm:ss')}</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Status Message (if any) */}
                        {dashData?.dashboardMessage && (
                            <div className="alert alert-info border-0 shadow-sm rounded-3 mb-4 d-flex align-items-center gap-2">
                                <i className="bi bi-info-circle-fill fs-5"></i>
                                {dashData.dashboardMessage}
                            </div>
                        )}

                        {/* Rendering the Logic-Engine Component */}
                        <StaffDoctorsList
                            staffDoctorsList={dashData?.staffDoctorsList || []}
                            canPrintToken={dashData?.canPrintToken}
                            token={token}
                            onRefresh={() => fetchDashboard(true)}
                        />
                    </div>

                    {/* Right Side Banner */}
                    <div className="col-lg-4">
                        <div className="sticky-top" style={{ top: '90px', height: '80vh' }}>
                            <img src={banner} className="img-fluid rounded-4 w-100 h-100" style={{ objectFit: 'contain', objectPosition: 'center' }} alt="Right Banner" />
                        </div>
                    </div>
                </div>

                {/* Mobile Layout */}
                <div className="d-lg-none">
                    {/* Main Content Area for Mobile */}
                    <div className="px-3">
                        {/* Compact Header Box */}
                        <div className="card border-0 shadow-sm rounded-4 mb-4 bg-white">
                            <div className="card-body py-2 px-3">
                                <div className="row align-items-center">
                                    <div className="col-12 text-center">
                                        <h6 className="mb-0 fw-bold text-dark" style={{ fontSize: '15px' }}>{userData?.name || dashData?.clinic_name || 'Doctor Dashboard'}</h6>
                                        <p className="text-muted mb-1 fw-bold" style={{ fontSize: '12px' }}>Live Overview & Token Management</p>
                                        <div className="border-top pt-1 mt-1">
                                            <p className="text-muted mb-1 fw-bold" style={{ fontSize: '12px' }}>{moment().format('dddd, DD MMMM YYYY')}</p>
                                            <p className="text-primary mb-0 fw-bold text-center" style={{ fontSize: '18px', fontFamily: 'monospace', letterSpacing: '2px' }}>{currentTime.format('HH:mm:ss')}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Status Message (if any) */}
                        {dashData?.dashboardMessage && (
                            <div className="alert alert-info border-0 shadow-sm rounded-3 mb-4 d-flex align-items-center gap-2">
                                <i className="bi bi-info-circle-fill fs-5"></i>
                                {dashData.dashboardMessage}
                            </div>
                        )}

                        {/* Rendering the Logic-Engine Component */}
                        <StaffDoctorsList
                            staffDoctorsList={dashData?.staffDoctorsList || []}
                            canPrintToken={dashData?.canPrintToken}
                            token={token}
                            onRefresh={() => fetchDashboard(true)}
                        />
                    </div>

                    {/* Mobile Banners at Bottom */}
                    <div className="mt-4 px-3">
                        <div className="row g-3">
                            {/* Left Banner for Mobile */}
                            <div className="col-6">
                                <img src={giphyImg} className="img-fluid rounded-4 w-100" style={{ height: 'auto', maxHeight: '250px', objectFit: 'contain' }} alt="Left Banner" />
                            </div>
                            {/* Right Banner for Mobile */}
                            <div className="col-6">
                                <img src={banner} className="img-fluid rounded-4 w-100" style={{ height: 'auto', maxHeight: '250px', objectFit: 'contain' }} alt="Right Banner" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Global Loader Backdrop (Only for manual triggers, not background polling) */}
            {isLoading && !dashData && (
                <div className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center"
                    style={{ backgroundColor: 'rgba(255,255,255,0.7)', zIndex: 9999 }}>
                    <div className="spinner-grow text-primary" style={{ width: '3rem', height: '3rem' }} role="status"></div>
                </div>
            )}

            <style>{`
                .dashboard-wrapper { font-family: 'Inter', sans-serif !important; }
                .dashboard-sticky-header { position: sticky; top: 0; z-index: 1020; transition: all 0.3s ease; }
                .card { transition: transform 0.2s ease, shadow 0.2s ease; border: none; }
                .card:hover { transform: translateY(-2px); box-shadow: 0 10px 20px rgba(0,0,0,0.08) !important; }
                .btn { transition: all 0.2s ease; }
                .btn:active { transform: scale(0.98); }
                .badge { font-weight: 600; }
                @media (max-width: 768px) {
                    .dashboard-sticky-header { top: 0; }
                    .offset-lg-2 { margin-left: 0; }
                }
            `}</style>
        </div>
    );
};

export default DoctorDashboard;
